import color from 'color';
import fluidRange from 'polished/lib/mixins/fluidRange';
import styled, { css } from 'styled-components';
import { darkColors, lightColors } from './themes';
import { PAGE_TITLE_RULES } from './typography';

// NOTE: there is intentionally no `colors` export here. Colors are now
// defined in themes.ts and must be accessed via the styled-components theme
// (as a theme prop in styled components, or via useTheme() in function
// components). A legacy `colors` export would silently freeze consumers on
// the light palette when the user switches to dark mode.

const DOT_GRID_SIZE = 5;
const DOT_GRID_HALF = DOT_GRID_SIZE / 2;

export const buildDotGridSvg = (dotColor) => {
  const encoded = encodeURIComponent(
    `<svg width='${DOT_GRID_SIZE}' height='${DOT_GRID_SIZE}' xmlns='http://www.w3.org/2000/svg'><circle cx='${DOT_GRID_HALF}' cy='${DOT_GRID_HALF}' r='0.5' fill='${dotColor}'/></svg>`,
  );
  return `url("data:image/svg+xml,${encoded}")`;
};

export const globalStyles = (theme) => `
  html, body, #root, #root>div:nth-child(1) {
    height: 100%
  }

  * {
    box-sizing: border-box;
  }

  body {
    position: relative;
    background-color: ${theme.surface};
    background-image: ${buildDotGridSvg(theme.surfaceTextureDot)};
    background-repeat: repeat;
    background-size: ${DOT_GRID_SIZE}px ${DOT_GRID_SIZE}px;
    margin: 0;
    padding: 0;
    font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Oxygen",
      "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-size: 14px;
    overflow-x: hidden;
    color: ${theme.textMain};
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, "Courier New", smonospace;
  }

  h1 {
    text-decoration: none;
    color: ${theme.textMain};
    font-size: 26px;
    ${PAGE_TITLE_RULES}
  }

  a {
    outline: none;
  }

  button {
    background: none;
    color: inherit;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
    outline: inherit;

    &::-moz-focus-inner {
      border: 0;
    }
  }

  textarea,
  input,
  input[type="text"],
  input[type="password"],
  input[type="button"],
  input[type="submit"] {
    -webkit-appearance: none;
    border-radius: 2px;
  }

  .ReactCollapse--collapse {
    transition: height 500ms;
  }

  .rc-slider-tooltip {
    z-index: 3;
  }

  .rc-slider-tooltip .rc-slider-tooltip-inner {
    background: none;
    box-shadow: none;
    font-size: 14px;
    color: ${theme.textMain};
  }
`;

export const breakpoints = {
  xs: '575',
  sm: '768',
  md: '990',
  lg: '1200',
  xl: '1440',
};

export const media = Object.keys(breakpoints).reduce((accumulator, label) => {
  accumulator[label] = (...args) => css`
    @media (max-width: ${breakpoints[label]}px) {
      ${css(...args)};
    }
  `;

  return accumulator;
}, {});

export const range = (
  prop,
  fromSize,
  toSize,
  minScreen = `${breakpoints.sm}px`,
  maxScreen = `${breakpoints.xl}px`,
) =>
  fluidRange(
    {
      prop,
      fromSize,
      toSize,
    },
    minScreen,
    maxScreen,
  );

// Partner overrides spread on top of the base palette. Tokens not in the base
// palette validate fine (the schema is open-ended) but won't render unless a
// styled-component reads them — keep override keys aligned with themes.ts.
export const buildTheme = (baseColors, partnerOverrides = {}) => {
  const colors = { ...baseColors, ...partnerOverrides };
  const surfaceRgb = color(colors.surface).rgb().array();
  const [r, g, b] = surfaceRgb.map(Math.round);

  return {
    ...colors,
    fadeMask: `rgba(${r}, ${g}, ${b}, 0.95)`,
    fadeMaskTransparent: `rgba(${r}, ${g}, ${b}, 0)`,
    surfaceOverlay60: `rgba(${r}, ${g}, ${b}, 0.6)`,
    surfaceGradient: `linear-gradient(to bottom, rgba(${r}, ${g}, ${b}, 0) 0%, rgba(${r}, ${g}, ${b}, 0.85) 15%, rgba(${r}, ${g}, ${b}, 1) 30%)`,
    ...media,
    range,
    breakpoints,
  };
};

// Default light theme for backwards compat
export const theme = buildTheme(lightColors);
export const lightTheme = theme;
export const darkTheme = buildTheme(darkColors);

export const Hidden = styled.div`
  ${({ size }) => media[size]`
    display: none;
  `}
`;

export const HiddenInline = styled.div`
  display: inline-block;

  ${({ size }) => media[size]`
    display: none;
  `}
`;

export const Visible = styled.div`
  display: none;

  ${({ size }) => media[size]`
    display: flex;
    margin: auto;
  `}
`;

export const VisibleInline = styled.div`
  display: none;

  ${({ size }) => media[size]`
    display: inline-block;
  `}
`;

export const visibleOnlyForSize = (size) => visibleOnlyForSizes([size]);
export const hiddenOnlyForSize = (size) => hiddenOnlyForSizes([size]);

export const visibleOnlyForSizes = (sizes) => {
  return Object.keys(breakpoints)
    .filter((s) => sizes.includes(s) === false)
    .map((size) => `hidden-${size}`)
    .join(' ');
};

export const hiddenOnlyForSizes = (sizes) => {
  return Object.keys(breakpoints)
    .filter((s) => sizes.includes(s) === true)
    .map((size) => `hidden-${size}`)
    .join(' ');
};
